import { Link } from "react-router-dom";
import { formatDateHuman, type DashboardView } from "@themislex/shared";
import { useApi } from "../../api/hooks";
import { Card, EmptyState, ErrorNote, MatterStatusBadge, Money, PageHeader, Spinner } from "../../components/ui";

export default function Dashboard() {
  const { data, error, loading } = useApi<DashboardView>("/api/dashboard");

  if (loading) {
    return (
      <div className="flex justify-center py-16 text-muted">
        <Spinner className="h-6 w-6" />
      </div>
    );
  }
  if (error || !data) {
    return <ErrorNote>{error ?? "The desk could not be loaded."}</ErrorNote>;
  }

  const stats = [
    { label: "Clients", value: String(data.clientCount) },
    { label: "Active matters", value: String(data.activeMatterCount) },
    { label: "Outstanding fees", value: <Money minor={data.outstandingMinor} /> },
    { label: "Collected this month", value: <Money minor={data.collectedThisMonthMinor} /> },
  ];

  return (
    <>
      <PageHeader
        title="Work desk"
        subtitle="A summary of the practice at a glance."
      />

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {stats.map((stat) => (
          <div key={stat.label} className="card p-5">
            <p className="text-xs font-semibold uppercase tracking-wide text-muted">
              {stat.label}
            </p>
            <p className="mt-2 font-heading text-2xl font-bold">{stat.value}</p>
          </div>
        ))}
      </div>

      <div className="mt-8">
        <Card
          title="Upcoming hearings"
          actions={
            <Link to="/desk/matters?sort=hearing" className="btn btn-ghost px-2 py-1 text-xs">
              All matters by hearing date
            </Link>
          }
        >
          {data.upcomingHearings.length === 0 ? (
            <EmptyState
              title="No hearings scheduled."
              hint="Matters with a next hearing date in the future will appear here, soonest first."
            />
          ) : (
            <div className="overflow-x-auto">
              <table className="table">
                <thead>
                  <tr>
                    <th scope="col">Date</th>
                    <th scope="col">Matter</th>
                    <th scope="col">Client</th>
                    <th scope="col">Court / forum</th>
                    <th scope="col">Case number</th>
                    <th scope="col">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {data.upcomingHearings.map((hearing) => (
                    <tr key={hearing.matterId}>
                      <td className="whitespace-nowrap font-medium">
                        {formatDateHuman(hearing.nextHearingDate)}
                      </td>
                      <td>
                        <Link
                          to={`/desk/matters/${hearing.matterId}`}
                          className="text-accent-text hover:underline"
                        >
                          {hearing.title}
                        </Link>
                      </td>
                      <td>
                        <Link
                          to={`/desk/clients/${hearing.clientId}`}
                          className="text-accent-text hover:underline"
                        >
                          {hearing.clientName}
                        </Link>
                      </td>
                      <td>{hearing.court ?? "—"}</td>
                      <td>{hearing.caseNumber ?? "—"}</td>
                      <td>
                        <MatterStatusBadge status={hearing.status} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>
    </>
  );
}
